import { getImportDataActionQueueKey } from "#src/batteries-included-components/Buttons/ImportDataAction/ImportDataAction.helpers";
import {
  uploadFileForDatasetImport,
  type DatasetFileImportRegistrationType,
} from "#src/utils/datasetFileImport";
import {
  useAuthenticatedContext,
  useIsFeatureAvailable,
} from "#src/contexts/AuthenticatedContext.helpers";
import { useQueueUnique } from "#src/hooks/useQueue";
import {
  Button,
  ButtonWithPopover,
  Dialog,
  Form,
  RadioInput,
  useForm,
  useToast,
} from "@validereinc/common-components";
import type {
  ResourceDefinitionType,
  TransactionType,
} from "@validereinc/domain";
import {
  ResourceDefinitions,
  Resources,
  TransactionAdapter,
} from "@validereinc/domain";
import React, { ReactNode, useEffect, useRef } from "react";
import {
  ImportDataActionProvider,
  useImportDataActionContext,
} from "./ImportDataActionContext/ImportDataActionContext";
import SingleEntityImportAction from "./SingleEntityImportAction/SingleEntityImportAction";
import EquipmentTemplatedConfigurationImportForm from "./TemplatedConfigurationsImportAction/EquipmentTemplatedConfigurationImportForm";

export const ImportDataActionContent = () => {
  const { claims } = useAuthenticatedContext();
  const { toast } = useToast();

  const {
    status,
    setStatus,
    openDialog,
    isDialogOpen,
    closeDialog,
    resource,
    resourceId,
    getTrigger,
    modes,
    datasetId,
  } = useImportDataActionContext();

  const [isTemplatedConfigurationsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:templated_configurations",
    permissionQuery: "templated_configuration_runs:write",
  });

  const queueKey = getImportDataActionQueueKey({
    resource,
    resourceId,
  });
  const [queue, setQueue] = useQueueUnique<TransactionType>(
    [],
    queueKey,
    (tx) => tx.transaction_id
  );

  // Form to render the radio buttons for choosing import type (classic / with templated config)
  const templatedConfigurationForm = useForm<{
    importMode?: "single-entity" | "templated-configuration";
  }>({
    defaultValues: {
      importMode:
        modes?.length &&
        modes[0] === "templated-configuration" &&
        isTemplatedConfigurationsAvailable
          ? "templated-configuration"
          : modes?.[0],
    },
  });

  const form = useForm<{
    file?: File[];
  }>({
    defaultValues: {
      file: undefined,
    },
  });

  const {
    getValues,
    formState: { isSubmitting },
  } = form;

  const selectedFile = form.watch("file");
  const importMode = templatedConfigurationForm.watch("importMode");

  useEffect(() => {
    form.setValue("file", undefined);
  }, [importMode]);

  useEffect(() => {
    if (isDialogOpen) {
      form.reset();
      templatedConfigurationForm.reset();
    }
  }, [isDialogOpen]);

  const lastRegistrationRef = useRef<DatasetFileImportRegistrationType | null>(
    null
  );

  const handleSubmit = async () => {
    const formValues = getValues();

    try {
      const file = formValues.file?.pop();

      if (!file || !datasetId || !claims) {
        throw new Error("No file provided");
      }

      const { transactionId } = await uploadFileForDatasetImport({
        file,
        datasetId,
        companyId: claims.company?.id,
        previousRegistration: lastRegistrationRef.current,
        onStatusChange: setStatus,
        onRegistered: (registration) => {
          lastRegistrationRef.current = registration;
        },
      });

      const transaction = await TransactionAdapter.getOne({
        id: transactionId,
        meta: { history: false },
      });

      if (!transaction?.data.length) {
        throw new Error("No transactions found for initiated transaction");
      }

      lastRegistrationRef.current = null;
      setQueue(queue.insert(transaction.data[0]));
      closeDialog();
      toast.push({
        intent: "info",
        description: "Successfully started data import",
      });
    } catch (err) {
      console.error(err);
      toast.push({
        intent: "error",
        description: "Failed to start data import. Try again?",
      });
    } finally {
      setStatus({ percentComplete: 0, label: "Ready" });
    }
  };

  const isValid = datasetId && selectedFile;

  const dialogActionRow = [
    <Button
      key="submit-action"
      variant="primary"
      type="submit"
      onClick={form.handleSubmit(handleSubmit)}
      isLoading={isSubmitting}
      disabled={!isValid || !!status?.percentComplete}
    >
      {status?.percentComplete ? status.label : "Import Data"}
    </Button>,
  ];
  const trigger = getTrigger ? (
    getTrigger({ handleClick: openDialog, isDialogOpen })
  ) : (
    <ButtonWithPopover
      buttonProps={{
        icon: "arrow-square-in",
        onClick: openDialog,
        isLoading: isDialogOpen,
      }}
      label="Import"
    />
  );

  const dialogTitle = `Import Data${
    resource.label.singular ? `: ${resource.label.singular}` : ""
  }${importMode === "templated-configuration" ? ` using ${ResourceDefinitions[Resources.TEMPLATED_CONFIGURATION].label.singular}` : ""}`;

  return (
    <>
      {trigger}
      <Dialog
        title={dialogTitle}
        isOpen={isDialogOpen}
        onClose={closeDialog}
        actionRow={dialogActionRow}
      >
        {modes && modes.length > 1 ? (
          <Form {...templatedConfigurationForm}>
            <RadioInput
              name="importMode"
              labelKey="label"
              valueKey="value"
              label="Import Type"
              isOptionalTextShown={false}
              options={[
                ...(modes?.includes("single-entity")
                  ? [
                      {
                        label: `${resource.label.singular} Data`,
                        value: "single-entity",
                      },
                    ]
                  : []),
                ...(isTemplatedConfigurationsAvailable &&
                modes?.includes("templated-configuration")
                  ? [
                      {
                        label: "Configuration Data",
                        value: "templated-configuration",
                      },
                    ]
                  : []),
              ]}
            />
          </Form>
        ) : null}
        <Form {...form}>
          {importMode === "single-entity" && <SingleEntityImportAction />}
          {importMode === "templated-configuration" && (
            <>
              {resource.id === Resources.EQUIPMENT && (
                <EquipmentTemplatedConfigurationImportForm />
              )}
            </>
          )}
        </Form>
      </Dialog>
    </>
  );
};

export const ImportDataAction = (props: ImportDataActionProps) => (
  <ImportDataActionProvider {...props}>
    <ImportDataActionContent />
  </ImportDataActionProvider>
);

export type ImportDataActionProps = {
  modes?: Array<"single-entity" | "templated-configuration">;
  resource: ResourceDefinitionType;
  resourceId?: string;
  preselectedTemplatedConfigurationName?: string;
  getTrigger?: ({
    handleClick,
    isDialogOpen,
  }: {
    handleClick: () => void;
    isDialogOpen: boolean;
  }) => ReactNode;
};
